import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { withStyles } from '@material-ui/core';
import TextField from '@components/TextFieldCaseSetting';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import styled from 'styled-components';
import AgGrid from '@components/AgGrid/AgGrid';
import { LinkCellRenderer } from '@trueprodigy/components';
import { resetAppealSearch } from '../actions/appeals';

const QuickAccessAppeal = ({
  history,
  classes,
  selectAppealRecord,
  quickAppealLookup,
}) => {
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(false);
  const firstKeyPressed = useRef(false);
  const [id, setId] = useState('');
  const [pID, setPID] = useState('');
  const [appeals, setAppeals] = useState([]);

  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    if(isOpen) {
      dispatch(resetAppealSearch());
      setAppeals([]);
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', keyHandler, false);
    return () => document.removeEventListener('keydown', keyHandler, false);
  }, []);

  const keyHandler = e => {
    let active = !(
      document.activeElement.id && document.activeElement.id.match(/cell/gi)
    );
    if (e.code === 'ArrowUp' && active) {
      if (!firstKeyPressed.current) {
        firstKeyPressed.current = true;
      } else {
        setOpen(true);
      }
    } else {
      firstKeyPressed.current = false;
    }

    setTimeout(() => {
      firstKeyPressed.current = false;
    }, 500);
  };

  const openAppeal = async () => {
    try {
      const res = await quickAppealLookup({
        page: 1,
        pageSize: 50,
        payload: {
          ...(id && { appealID: [id]} ),
          ...(pID && { pIDList: [pID] }),
        },
      });
      if (res && res.results && res.results.length === 1) {
        const { appealID } = res.results[0];
        selectAppealRecord(appealID);
        closeModal();
        history.push(`/appeals/${appealID}/summary`);
      } else if (res && res.results && res.results.length > 1) {
        setAppeals(res.results);
      } else {
        setIsInvalid(true);
      }
    } catch (error) {
      setIsInvalid(true);
    }
  };

  const closeModal = () => {
    setId('');
    setPID('');
    setOpen(false);
    setAppeals([]);
    setIsInvalid(false);
  };

  const buildColDefs = () => [
    {
      headerName: 'Appeal ID',
      field: 'appealID',
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: (_, data) => {
          const { appealID } = data;
          selectAppealRecord(appealID);
          closeModal();
          history.push(`/appeals/${appealID}/summary`);
        },
      },
    },
    {
      headerName: 'Year',
      field: 'pYear',
      sortable: true,
    },
    {
      headerName: 'Prop ID',
      field: 'pID',
    },
    {
      headerName: 'Type',
      field: 'appealType',
    },
    {
      headerName: 'Status',
      field: 'appealStatus',
    },
    {
      headerName: 'Finalized',
      field: 'appealsFinalized',
    }
  ];

  return (
    <Drawer
      anchor="top"
      open={isOpen}
      onClose={closeModal}
      PaperProps={{ style: { width: '50%', left: 'calc(100vw / 4)' } }}
    >
      <StyledDialogTitle id="form-dialog-title">
        Appeal Look-up
      </StyledDialogTitle>
      <DialogContent style={{ paddingBottom: 0, paddingLeft: 35 }}>
        <div
          style={{
            display: 'flex',
            gap: '20px',
            position: 'relative',
            padding: '20px 0 0 0',
            width: '100%'
          }}
        >
          <TextField
            autoFocus
            margin="dense"
            id="id"
            type="text"
            style={{ width: '100%' }}
            value={id}
            placeholder="Type Case ID and Press Enter"
            onChange={e => {
              if (isInvalid) setIsInvalid(false);
              setId(e.target.value);
            }}
            InputProps={{
              classes: { input: classes.input, root: classes.root },
            }}
            onKeyPress={e => {
              if (e.key === 'Enter' && id) {
                openAppeal();
                e.preventDefault();
              }
            }}
          />
          <TextField
            autoFocus
            margin="dense"
            id="pID"
            type="text"
            style={{ width: '100%' }}
            value={pID}
            placeholder="Type Property ID and Press Enter"
            onChange={e => {
              if (isInvalid) setIsInvalid(false);
              setPID(e.target.value);
            }}
            InputProps={{
              classes: { input: classes.input, root: classes.root },
            }}
            onKeyPress={e => {
              if (e.key === 'Enter' && appeals.length > 0) {
                let sortedAppealsList = JSON.parse(JSON.stringify(appeals));
                sortedAppealsList.sort(function(a, b){return Number(b.pYear)-Number(a.pYear)})
                selectAppealRecord(sortedAppealsList[0].appealID);
                closeModal();
                setTimeout(() => {
                  history.push(`/appeals/${sortedAppealsList[0].appealID}/summary`);
                }, 100);
              }
              if (e.key === 'Enter' && pID) {
                openAppeal();
                e.preventDefault();
              }
            }}
          />
          <div style={{ width: '20px' }}></div>
        </div>
      </DialogContent>
      <DialogActions
        style={{
          display: 'flex',
          justifyContent: isInvalid ? 'space-between' : 'flex-end',
          alignItems: 'center',
          padding: '15px 25px 10px 42px',
          position: 'relative',
        }}
      >
        {isInvalid && <ErrorTip>Invalid Case ID</ErrorTip>}
        {
          appeals.length > 0 ? (
            <PropertyIdTip>
              Press (<span>enter</span>) to pull the most current appeal
            </PropertyIdTip>
          ) : null
        }
        <div>
          <Button onClick={closeModal} color="primary">
            Cancel
          </Button>
          <Button onClick={openAppeal} color="primary">
            Lookup
          </Button>
        </div>
      </DialogActions>
      {appeals.length > 1 ? (
        <div
          style={{
            width: '100%',
            height: '300px',
            padding: '20px',
          }}
          className="ag-theme-material"
        >
          <AgGrid
            autoResizeToFit
            rowData={appeals}
            columnDefs={buildColDefs()}
            name="quickAppealLookupListing"
            disablePreferenceSave
            onGridReady={params => {
              const columnState = {
                state: [
                  {
                    colId: 'pYear',
                    sort: 'desc',
                  }
                ]
              }
              params.columnApi.applyColumnState(columnState)
            }}
          />
        </div>
      ) : null}
    </Drawer>
  );
};

QuickAccessAppeal.propTypes = {
  history: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  selectAppealRecord: PropTypes.func.isRequired,
  quickAppealLookup: PropTypes.func.isRequired,
};

const ErrorTip = styled.p`
  margin: 0;
  color: red;
`;

const PropertyIdTip = styled.div`
  position: absolute;
  top: 0;
  right: 55px;
  color: #333333;
  font-style: italic;
  font-family: Arial;
  font-size: 10px;

  span {
    color: #4655b1;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  padding: 12px 35px 0 !important;

  > h2 {
    font-family: Roboto, sans-serif;
    font-size: 32px;
    font-weight: bold;
  }
`;

const styles = {
  root: {
    lineHeight: '30px',
  },
  input: {
    '&::placeholder': {
      fontSize: 25,
      fontFamily: 'Arial',
      color: 'rgba(0, 0, 0, 0.16)',
    },
    font: '300 30px Roboto, sans-serif',
  },
};

export default withStyles(styles)(QuickAccessAppeal);
